import Header from "../components/Header";
import Footer from "../components/Footer";
import ContentBlockRenderer from "../components/ContentBlockRenderer";
import { usePage, useSeo, cmsMediaUrl } from "../lib/cms";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * Generic CMS-driven page used for /over-ons, /services, /events, /contact.
 * Renders hero (from page) + dynamic blocks (from CMS).
 */
export const CmsContentPage = ({ pageKey, fallbackHero, contactSlot, dataTestId }) => {
  const { page, loading } = usePage(pageKey);

  useSeo({
    title: page?.seo_title || page?.title,
    description: page?.seo_description,
  });

  const heroTitle = page?.hero_title || page?.title || "";
  const heroSubtitle = page?.hero_subtitle || "";
  const introText = page?.intro_text || "";
  const bodyText = page?.body_text || "";
  const ctaText = page?.cta_text;
  const ctaLink = page?.cta_link;
  const heroImage = cmsMediaUrl(page?.hero_image) || fallbackHero;

  return (
    <div
      data-testid={dataTestId || `cms-page-${pageKey}`}
      className="min-h-screen bg-ink-950"
    >
      <Header />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden border-b border-white/[0.06]">
          {heroImage && (
            <div className="absolute inset-0">
              <img
                src={heroImage}
                alt={page?.hero_image?.alt_text || heroTitle}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-ink-950/80 via-ink-950/55 to-ink-950" />
              <div className="absolute inset-0 noise-overlay" />
            </div>
          )}
          <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-12 pt-24 pb-24 md:pt-32 md:pb-32">
            <div className="max-w-3xl">
              {heroSubtitle && (
                <div className="flex items-center gap-3 mb-8 animate-fade-in">
                  <span className="h-px w-10 bg-champagne" />
                  <span className="text-[11px] tracking-[0.3em] uppercase text-champagne font-body">
                    {heroSubtitle}
                  </span>
                </div>
              )}
              <h1
                data-testid={`${dataTestId || `cms-page-${pageKey}`}-hero-title`}
                className="font-display text-white text-6xl sm:text-7xl md:text-8xl lg:text-[8rem] font-light leading-[0.95] tracking-tight animate-fade-up"
              >
                {heroTitle}
              </h1>
              {introText && (
                <p
                  data-testid={`${dataTestId || `cms-page-${pageKey}`}-intro`}
                  className="mt-8 max-w-2xl text-base md:text-lg text-zinc-300 font-body leading-relaxed animate-fade-up"
                  style={{ animationDelay: "120ms" }}
                >
                  {introText}
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Body */}
        {bodyText && (
          <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-12 py-16 md:py-24">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
              <div className="md:col-span-7 md:col-start-3">
                <p className="text-base md:text-lg text-zinc-300 font-body leading-relaxed whitespace-pre-line">
                  {bodyText}
                </p>
                {ctaText && ctaLink && (
                  <Link
                    to={ctaLink}
                    className="inline-flex items-center gap-2 mt-10 px-7 py-3.5 border border-champagne text-champagne hover:bg-champagne hover:text-ink-950 transition-colors text-[11px] tracking-[0.3em] uppercase font-body font-semibold"
                  >
                    {ctaText}
                    <ArrowRight size={13} />
                  </Link>
                )}
              </div>
            </div>
          </section>
        )}

        {/* CMS blocks */}
        {!loading &&
          page?.blocks?.map((block) => (
            <ContentBlockRenderer key={block.id} block={block} />
          ))}

        {/* Custom slot (e.g. contact form on /contact) */}
        {contactSlot}

        {/* Empty state */}
        {!loading && !page && (
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 text-center">
            <p className="text-sm text-zinc-500 font-body">
              Deze pagina is momenteel niet beschikbaar.
            </p>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default CmsContentPage;
